/**
 * Portal design-system primitives.
 *
 * The language is the marketing site's, translated to a dashboard: warm paper,
 * hairline rules, monospace labels, one confident display face, and the data as
 * the only saturated colour. Deliberately not generic SaaS — no gradients, no
 * glow, no drop-shadow stacks, no dark mode.
 *
 * Kept deliberately small. Anything a single screen needs once belongs in that
 * screen, not here.
 *
 * @see apps/website/components/site/kit.tsx — the source of the language.
 */

import * as React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

/* ------------------------------------------------------------------ */
/* Tokens                                                              */
/* ------------------------------------------------------------------ */

/** Warm paper — the app surface. */
export const PAPER = '#f6f4ef';
/** The single dark surface (sidebar, primary buttons). Never pure black. */
export const INK = '#1e2228';

/* ------------------------------------------------------------------ */
/* Type                                                                */
/* ------------------------------------------------------------------ */

/** Small monospace label. The system's most recognisable mark. */
export function Eyebrow({
  children,
  tone = 'dark',
  className,
}: {
  children: React.ReactNode;
  tone?: 'dark' | 'light';
  className?: string;
}) {
  return (
    <div
      className={cn(
        'font-mono text-[11px] uppercase tracking-eyebrow',
        tone === 'dark' ? 'text-zinc-500' : 'text-zinc-400',
        className
      )}
    >
      {children}
    </div>
  );
}

export function H1({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <h1
      className={cn(
        'font-display text-[1.75rem] font-bold leading-[1.1] tracking-[-0.03em] text-primary sm:text-[2rem]',
        className
      )}
    >
      {children}
    </h1>
  );
}

export function H2({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <h2 className={cn('font-display text-xl font-bold tracking-tight text-primary', className)}>
      {children}
    </h2>
  );
}

export function H3({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <h3 className={cn('font-display text-base font-bold tracking-tight text-primary', className)}>
      {children}
    </h3>
  );
}

/** Short rule under a page heading. */
export function Rule({ className }: { className?: string }) {
  return <div className={cn('h-[3px] w-12 rounded-full bg-primary', className)} />;
}

/** Monospace inline datum — URLs, ids, counts, timestamps. */
export function Mono({ children, className }: { children: React.ReactNode; className?: string }) {
  return <span className={cn('font-mono text-[13px] text-zinc-600', className)}>{children}</span>;
}

/* ------------------------------------------------------------------ */
/* Page header                                                         */
/* ------------------------------------------------------------------ */

/**
 * The standard screen opener: monospace eyebrow, display title, accent rule,
 * and an actions slot that stays on the baseline of the title.
 */
export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  className,
}: {
  eyebrow?: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('mb-8', className)}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          {eyebrow && <Eyebrow className="mb-3">{eyebrow}</Eyebrow>}
          <H1>{title}</H1>
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      <Rule className="mt-5" />
      {description && (
        <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-muted">{description}</p>
      )}
    </div>
  );
}

/** Eyebrow + heading opener for a block inside a page. */
export function SectionHead({
  eyebrow,
  title,
  aside,
  className,
}: {
  eyebrow: string;
  title?: React.ReactNode;
  aside?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap items-end justify-between gap-3', className)}>
      <div className="min-w-0">
        <Eyebrow>{eyebrow}</Eyebrow>
        {title && <H2 className="mt-2">{title}</H2>}
      </div>
      {aside && <div className="flex items-center gap-2">{aside}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Controls                                                            */
/* ------------------------------------------------------------------ */

export type PillTone = 'solid' | 'outline' | 'ghost' | 'danger' | 'onInk' | 'outlineOnInk';

const PILL_TONE: Record<PillTone, string> = {
  solid: 'bg-primary text-white hover:scale-[1.02] active:scale-[0.99] disabled:hover:scale-100',
  outline: 'bg-white/60 text-primary ring-1 ring-zinc-300 hover:bg-white hover:ring-zinc-400',
  ghost: 'text-zinc-600 hover:bg-zinc-900/5 hover:text-primary',
  danger: 'bg-white text-red-700 ring-1 ring-red-600/30 hover:bg-red-50',
  onInk: 'bg-white text-ink hover:scale-[1.02] active:scale-[0.99]',
  outlineOnInk: 'text-zinc-300 ring-1 ring-white/15 hover:bg-white/5 hover:text-white',
};

const PILL_SIZE = {
  sm: 'px-3.5 py-1.5 text-[13px]',
  md: 'px-5 py-2.5 text-sm',
  lg: 'px-6 py-3 text-base',
} as const;

type PillProps = {
  tone?: PillTone;
  size?: keyof typeof PILL_SIZE;
  arrow?: boolean;
  className?: string;
  children: React.ReactNode;
  /** Internal route — renders a react-router <Link>. */
  to?: string;
  /** External/absolute URL — renders an <a>. */
  href?: string;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'>;

/**
 * The system's only button shape: a full-round pill. Solid ink for the one
 * primary action per view, hairline outline for everything else.
 */
export function Pill({
  tone = 'solid',
  size = 'md',
  arrow = false,
  className,
  children,
  to,
  href,
  ...props
}: PillProps) {
  const cls = cn(
    'inline-flex items-center justify-center gap-2 rounded-full font-medium transition-all duration-200 disabled:pointer-events-none disabled:opacity-50',
    PILL_TONE[tone],
    PILL_SIZE[size],
    className
  );

  const inner = (
    <>
      {children}
      {arrow && <span aria-hidden>→</span>}
    </>
  );

  // `onClick` rides along on the link forms too — a nav pill inside a drawer
  // usually has to dismiss it on the way out, and silently dropping the handler
  // leaves the panel hanging open over the new route.
  if (to) {
    return (
      <Link to={to} className={cls} onClick={props.onClick as React.MouseEventHandler}>
        {inner}
      </Link>
    );
  }
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={cls}
        onClick={props.onClick as React.MouseEventHandler}
      >
        {inner}
      </a>
    );
  }
  return (
    <button type="button" className={cls} {...props}>
      {inner}
    </button>
  );
}

/** Understated inline link with the system's underline behaviour. */
export function TextLink({
  to,
  href,
  children,
  className,
}: {
  to?: string;
  href?: string;
  children: React.ReactNode;
  className?: string;
}) {
  const cls = cn(
    'inline-flex items-center gap-1.5 text-[14px] font-medium text-primary underline-offset-4 hover:underline',
    className
  );
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to ?? '#'} className={cls}>
      {children}
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/* Status                                                              */
/* ------------------------------------------------------------------ */

export type TagTone = 'neutral' | 'live' | 'warn' | 'danger' | 'info' | 'violet' | 'ink';

const TAG_TONE: Record<TagTone, string> = {
  neutral: 'bg-zinc-100 text-zinc-600 ring-zinc-900/5',
  live: 'bg-emerald-50 text-emerald-800 ring-emerald-600/20',
  warn: 'bg-amber-50 text-amber-800 ring-amber-600/20',
  danger: 'bg-red-50 text-red-800 ring-red-600/20',
  info: 'bg-sky-50 text-sky-800 ring-sky-600/20',
  violet: 'bg-violet-50 text-violet-800 ring-violet-600/20',
  ink: 'bg-primary text-white ring-transparent',
};

/** Monospace status chip. Colour carries meaning here and nowhere else. */
export function Tag({
  tone = 'neutral',
  dot = false,
  children,
  className,
}: {
  tone?: TagTone;
  dot?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex h-5 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 font-mono text-[11px] uppercase leading-none tracking-[0.08em] ring-1',
        TAG_TONE[tone],
        className
      )}
    >
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Surfaces                                                            */
/* ------------------------------------------------------------------ */

/** Hairline-bordered white surface. The dashboard's workhorse container. */
export function Panel({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'rounded-xl border border-zinc-200 bg-white transition-colors duration-200',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/** Monospace-labelled header rule inside a Panel. */
export function PanelHeader({
  title,
  actions,
  className,
}: {
  title: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 border-b border-zinc-200 px-5 py-3',
        className
      )}
    >
      <Eyebrow>{title}</Eyebrow>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function PanelBody({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={cn('p-5', className)}>{children}</div>;
}

/**
 * Hairline-separated grid: 1px gaps over a zinc background, children opaque.
 * The site's signature layout device — use it instead of a row of shadowed
 * cards whenever the items are peers.
 */
export function HairlineGrid({
  cols = 'sm:grid-cols-3',
  className,
  children,
}: {
  cols?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn('grid gap-px overflow-hidden rounded-xl bg-zinc-200', cols, className)}
    >
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Stats                                                               */
/* ------------------------------------------------------------------ */

/**
 * One number in a HairlineGrid cell: monospace label above, display figure
 * below, optional delta and footnote. No icon tiles, no coloured backgrounds.
 */
export function Stat({
  label,
  value,
  delta,
  deltaTone = 'neutral',
  note,
  className,
}: {
  label: string;
  value: React.ReactNode;
  delta?: React.ReactNode;
  deltaTone?: 'up' | 'down' | 'neutral';
  note?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('bg-white p-5', className)}>
      <Eyebrow>{label}</Eyebrow>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="font-display text-[1.75rem] font-bold leading-none tracking-[-0.03em] text-primary">
          {value}
        </span>
        {delta != null && (
          <span
            className={cn(
              'font-mono text-[12px]',
              deltaTone === 'up'
                ? 'text-emerald-700'
                : deltaTone === 'down'
                  ? 'text-red-700'
                  : 'text-zinc-500'
            )}
          >
            {delta}
          </span>
        )}
      </div>
      {note && <p className="mt-2 text-[13px] leading-relaxed text-muted">{note}</p>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Tables                                                              */
/* ------------------------------------------------------------------ */

export function Th({
  className,
  children,
  ...props
}: React.ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      scope="col"
      className={cn(
        'whitespace-nowrap border-b border-zinc-200 px-4 py-2.5 text-left font-mono text-[11px] font-normal uppercase tracking-[0.14em] text-zinc-500',
        className
      )}
      {...props}
    >
      {children}
    </th>
  );
}

export function Td({
  className,
  children,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td
      className={cn('border-b border-zinc-100 px-4 py-3.5 align-middle text-[14px]', className)}
      {...props}
    >
      {children}
    </td>
  );
}

/* ------------------------------------------------------------------ */
/* Marks (line-art illustrations)                                      */
/* ------------------------------------------------------------------ */

const MARK_SIZES = {
  sm: 'h-20 w-20',
  md: 'h-32 w-32', // beside a heading, in a block
  lg: 'h-44 w-44', // the subject of an empty state
  xl: 'h-56 w-56', // the subject of a whole page
} as const;

/**
 * An engraved line-art object from the marketing site's vocabulary.
 *
 * The source PNGs are ink on white paper, so `mix-blend-multiply` drops the
 * ground out and lets the drawing sit on the warm surface instead of showing
 * as a white patch. Purely decorative — always hidden from assistive tech.
 *
 * The brightness lift is what makes multiply actually vanish: the generated
 * paper is a shade under pure white, and on a white section that residue reads
 * as a grey box around every drawing. Lifting clips it to white while leaving
 * the ink alone — black stays black under a multiply.
 *
 * `tone="dark"` is the same trick upside down: invert turns the ink white and
 * the paper black, then `mix-blend-screen` drops the black away. The extra
 * contrast is not taste — the source paper is a hair off pure white, so without
 * it the inverted ground stays a visible grey rectangle instead of vanishing.
 * Needed because the invite page runs ink-dark sections multiply would swallow.
 */
export function Mark({
  src,
  size = 'md',
  tone = 'light',
  className,
}: {
  src: string;
  size?: keyof typeof MARK_SIZES;
  tone?: 'light' | 'dark';
  className?: string;
}) {
  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      width={320}
      height={320}
      loading="lazy"
      className={cn(
        'shrink-0',
        tone === 'dark'
          ? 'invert contrast-[1.35] mix-blend-screen'
          : 'brightness-[1.08] mix-blend-multiply',
        MARK_SIZES[size],
        className
      )}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Empty state                                                         */
/* ------------------------------------------------------------------ */

/**
 * The nothing-here state — one design, used everywhere.
 *
 * An empty screen is the first thing a new customer sees, so it carries the
 * drawing at full size and answers three things in order: what is missing, what
 * will fill it, and the one action worth taking. `hint` is the mechanical
 * footnote — where the rows actually come from — set in monospace below a
 * hairline, the way the marketing site handles an aside.
 *
 * `mark` is optional: a transient state (a filter that matched nothing) reads
 * better without a drawing, and nothing should ever stack two of them.
 */
export function Empty({
  mark,
  eyebrow,
  title,
  description,
  action,
  hint,
  framed = false,
  compact = false,
  className,
}: {
  mark?: string;
  eyebrow?: React.ReactNode;
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
  hint?: React.ReactNode;
  /** Draw the card chrome. Off when the caller already sits inside a Panel. */
  framed?: boolean;
  /** Tighter padding, for an empty state that shares a screen with something else. */
  compact?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'text-center',
        // The drawing carries the vertical presence, so the padding is lighter
        // than it would be for a text-only block.
        compact ? 'px-6 py-10' : 'px-6 py-12 sm:py-14',
        framed && 'rounded-xl border border-zinc-200 bg-white',
        className
      )}
    >
      {mark && <Mark src={mark} size={compact ? 'md' : 'lg'} className="mx-auto" />}
      <div className={cn('mx-auto h-px w-10 bg-zinc-300', mark ? 'mt-6 mb-5' : 'mb-5')} />
      {eyebrow && <Eyebrow className="mb-2.5">{eyebrow}</Eyebrow>}
      <h3
        className={cn(
          'font-display font-bold tracking-tight text-primary',
          compact ? 'text-lg' : 'text-xl sm:text-[1.375rem]'
        )}
      >
        {title}
      </h3>
      {description && (
        <p className="mx-auto mt-2.5 max-w-sm text-[14px] leading-relaxed text-muted">
          {description}
        </p>
      )}
      {action && <div className="mt-7 flex justify-center">{action}</div>}
      {hint && (
        <div className="mx-auto mt-9 max-w-sm border-t border-zinc-200 pt-4">
          <p className="font-mono text-[11px] leading-relaxed text-zinc-400">{hint}</p>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Forms                                                               */
/* ------------------------------------------------------------------ */

/** Monospace field label + control + hint/error, on the system's spacing. */
export function Field({
  label,
  hint,
  error,
  htmlFor,
  children,
  className,
}: {
  label: string;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  htmlFor?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('space-y-2', className)}>
      <label
        htmlFor={htmlFor}
        className="block font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500"
      >
        {label}
      </label>
      {children}
      {error ? (
        <p className="text-[13px] text-red-700">{error}</p>
      ) : hint ? (
        <p className="text-[13px] text-muted">{hint}</p>
      ) : null}
    </div>
  );
}
